"use client";

import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Compass, Hammer, Layers, Lightbulb } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
const capabilities = [{
  id: 1,
  title: "Architectural trim & millwork",
  description: "Handcrafted coffered ceilings, custom wainscoting, and historic casing profiles tailored to your home.",
  iconName: "Hammer"
}, {
  id: 2,
  title: "Hardwood floors & refinishing",
  description: "Solid hardwood installations and dust-controlled refinishing that preserves rich natural grain.",
  iconName: "Layers"
}, {
  id: 3,
  title: "Open-concept structural changes",
  description: "Engineered load-bearing wall removal and steel beam integration for natural, continuous light.",
  iconName: "Compass"
}, {
  id: 4,
  title: "Layered lighting design",
  description: "Architectural recessed illumination, zone dimming systems, and bespoke designer fixture placement.",
  iconName: "Lightbulb"
}];
export default function InteriorRemodeling() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  const renderIcon = (name: string) => {
    switch (name) {
      case "Hammer":
        return <Hammer className="size-5 text-chart-1 stroke-[1.75]" />;
      case "Layers":
        return <Layers className="size-5 text-chart-1 stroke-[1.75]" />;
      case "Compass":
        return <Compass className="size-5 text-chart-1 stroke-[1.75]" />;
      case "Lightbulb":
        return <Lightbulb className="size-5 text-chart-1 stroke-[1.75]" />;
      default:
        return <Hammer className="size-5 text-chart-1 stroke-[1.75]" />;
    }
  };
  return <section id="interior-remodeling" data-nav="light" className="relative bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border overflow-hidden">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Left Column (5/12 on large screens) */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-5 flex flex-col justify-center space-y-6">
            <div>
              <Badge variant="outline" className="bg-secondary text-secondary-foreground border-border uppercase tracking-widest text-xs font-semibold px-3 py-1 rounded-sm mb-3">
                Tailored Craftsmanship
              </Badge>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-4xl font-semibold leading-tight text-foreground">
                Tailored interior spaces for modern Decatur living
              </h2>
            </div>

            <p className="font-sans text-base sm:text-lg text-muted-foreground leading-relaxed">
              Open up your floor plan, renew original floors, and add custom millwork built to last. Our Decatur team balances historic neighborhood character with clean structural sightlines and durable materials.
            </p>

            {/* 2x2 Capabilities Grid */}
            <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              {capabilities.map((item, i) => <div key={item.id} data-index={i}>
                  <motion.div variants={itemVariants} whileHover={{
                y: -4
              }} transition={{
                duration: 0.3,
                ease: "easeOut"
              }} className="h-full">
                    <Card className="h-full bg-card/85 backdrop-blur-md border border-border rounded-xl p-5 shadow-xs transition-[border-color,box-shadow] duration-300 hover:border-chart-1/40 hover:shadow-md">
                      <CardContent className="p-0 flex flex-col space-y-3">
                        <div className="flex items-center justify-center size-10 rounded-md bg-secondary border border-border">
                          {renderIcon(item.iconName)}
                        </div>
                        <div>
                          <h3 className="font-sans text-base font-semibold text-foreground tracking-tight mb-1">
                            {item.title}
                          </h3>
                          <p className="font-sans text-xs sm:text-sm text-muted-foreground leading-relaxed">
                            {item.description}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                </div>)}
            </motion.div>
          </motion.div>

          {/* Right Column (7/12 on large screens) */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: 0.1,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-7 flex flex-col space-y-6">
            <div className="relative rounded-xl overflow-hidden border border-border shadow-sm bg-card group">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/interior_remodeling_living_room.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/interior_remodeling_living_room.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/interior_remodeling_living_room.png-wvc-sizes" alt="Renovated open-concept living and dining space with natural oak hardwood floors" loading="lazy" decoding="async" className="w-full h-auto max-h-[540px] object-cover object-center" />

              {/* Layered frosted glass caption card */}
              <div className="sm:absolute sm:bottom-6 sm:left-6 sm:right-6 m-4 sm:m-0 p-5 rounded-lg bg-card/90 backdrop-blur-md border border-border shadow-md">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="space-y-1">
                    <p className="text-xs font-bold uppercase tracking-wider text-chart-1">
                      Featured Living Space
                    </p>
                    <p className="font-serif text-lg font-semibold text-foreground">
                      Full Main-Level Renovation
                    </p>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      Structural beam install, white oak flooring, and custom fireplace built-ins.
                    </p>
                  </div>
                  <div className="shrink-0">
                    <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-5 py-2.5 font-semibold text-sm shadow-sm transition-[background-color,transform] duration-200">
                      <Link to="#" className="inline-flex items-center gap-2">
                        <span>Plan your remodel</span>
                        <ArrowRight className="size-4" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick architectural assurance strip */}
            <div className="grid grid-cols-3 gap-4 pt-2 border-t border-border">
              <div className="space-y-1">
                <p className="font-serif text-lg sm:text-xl font-semibold text-foreground">100%</p>
                <p className="text-xs text-muted-foreground">Decatur Permitted</p>
              </div>
              <div className="space-y-1">
                <p className="font-serif text-lg sm:text-xl font-semibold text-foreground">Direct</p>
                <p className="text-xs text-muted-foreground">Craftsman Oversight</p>
              </div>
              <div className="space-y-1">
                <p className="font-serif text-lg sm:text-xl font-semibold text-foreground">Clear</p>
                <p className="text-xs text-muted-foreground">Written Scope</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}